import { useProjectData } from '@hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from '@hooks/useSendGpnAnalyticsEvent';
import { Button, ContextMenu, IconArrowDown } from '@uikit';

import type { FC } from 'react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import Styles from '../BimCalculationLimited.module.css';
import { CreatingTypes } from '../commonData';

interface IBimCalculationLimitedToolbar {
    isDisabled: boolean;
    onCreateClick: (creatingType?: CreatingTypes, isAdditionalTransport?: boolean) => void;
}

export const BimCalculationLimitedToolbar: FC<IBimCalculationLimitedToolbar> = ({ isDisabled, onCreateClick }) => {
    const { id: projectId } = useParams();
    const { projectData } = useProjectData(projectId, false);
    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const ref = useRef(null);
    const additionalTransportRef = useRef(null);
    const [isContextMenuOpen, setIsContextMenuOpen] = useState<boolean>(false);
    const [isAdditionalTransportContextMenuOpen, setIsAdditionalTransportContextMenuOpen] = useState<boolean>(false);

    const handleContextToggle = useCallback(() => {
        setIsContextMenuOpen((prev) => !prev);
    }, []);

    const handleAdditionalTransportContextToggle = useCallback(() => {
        setIsAdditionalTransportContextMenuOpen((prev) => !prev);
    }, []);

    const createActions = useMemo(
        () => [
            {
                label: 'Вручную',
                action: () => {
                    onCreateClick(CreatingTypes.MANUAL);
                    handleContextToggle();
                },
            },
            {
                label: 'Из справочника прочих и лимитированных затрат',
                action: () => {
                    onCreateClick(CreatingTypes.BOOK);
                    handleContextToggle();
                },
            },
        ],
        [handleContextToggle, onCreateClick],
    );

    const createActionsForAdditionalTransportButton = useMemo(
        () => [
            {
                label: 'Вручную',
                action: () => {
                    onCreateClick(CreatingTypes.MANUAL, true);
                    handleAdditionalTransportContextToggle();
                },
            },
            {
                label: 'Из справочника прочих и лимитированных затрат',
                action: () => {
                    onCreateClick(CreatingTypes.BOOK, true);
                    handleAdditionalTransportContextToggle();
                },
            },
        ],
        [handleAdditionalTransportContextToggle, onCreateClick],
    );

    const createBtnHandler = useCallback(() => {
        handleContextToggle();

        sendGpnAnalyticsEvent({
            componentId: 'rim_create bim limited create button_pressed',
            projectId,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
            methodology: projectData?.methodology,
        });
    }, [
        handleContextToggle,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        sendGpnAnalyticsEvent,
    ]);

    const createAdditionalTransportBtnHandler = useCallback(() => {
        handleAdditionalTransportContextToggle();

        sendGpnAnalyticsEvent({
            componentId: 'rim_create bim limited additional transport create button_pressed',
            projectId,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
            methodology: projectData?.methodology,
        });
    }, [
        handleAdditionalTransportContextToggle,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        sendGpnAnalyticsEvent,
    ]);

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
                ref={ref}
                style={{ marginRight: '8px', width: 230 }}
                label="Создать лимитированные"
                view="secondary"
                size="s"
                disabled={isDisabled}
                onClick={createBtnHandler}
                iconRight={IconArrowDown}
            />
            <Button
                ref={additionalTransportRef}
                style={{ marginRight: '8px', width: 220 }}
                label="Создать доп. транспорт"
                view="secondary"
                size="s"
                disabled={isDisabled}
                onClick={createAdditionalTransportBtnHandler}
                iconRight={IconArrowDown}
            />
            <ContextMenu
                isOpen={isContextMenuOpen}
                anchorRef={ref}
                getItemLabel={({ label }) => label}
                items={createActions}
                onClickOutside={handleContextToggle}
                direction="downStartLeft"
                getItemOnClick={({ action }) => action}
                style={{
                    width: 230,
                }}
                className={Styles.toolbarAddButton_contextMenu}
            />
            <ContextMenu
                isOpen={isAdditionalTransportContextMenuOpen}
                anchorRef={additionalTransportRef}
                getItemLabel={({ label }) => label}
                items={createActionsForAdditionalTransportButton}
                onClickOutside={handleAdditionalTransportContextToggle}
                direction="downStartLeft"
                getItemOnClick={({ action }) => action}
                style={{
                    width: 230,
                }}
                className={Styles.toolbarAddButton_contextMenu}
            />
        </div>
    );
};

BimCalculationLimitedToolbar.displayName = 'BimCalculationLimitedToolbar';
